@if (response) {
  <article class="response-editor" aria-labelledby="response-editor-title">
    <header class="editor-header">
      <div class="editor-heading">
        <span class="editor-eyebrow">Selected response</span>
        <h2 id="response-editor-title">Response from {{senderLabel}}</h2>
        <span class="editor-location">{{locationLabel}}</span>
      </div>
      <button type="button" class="close-editor" aria-label="Close editor" (click)="Cancel()" title="Close editor">
        <svg class="cms-icon" aria-hidden="true" focusable="false"><use href="/images/cms-actions.svg#close"></use></svg>
        <span>Close</span>
      </button>
    </header>

    <div class="editor-content">
      <section class="editor-section summary-section" aria-labelledby="response-summary-title">
        <h3 id="response-summary-title">Response summary</h3>
        <dl class="metadata-grid">
          <div class="metadata-item">
            <dt>Sender</dt>
            <dd>{{senderLabel}}</dd>
          </div>
          <div class="metadata-item">
            <dt>Location</dt>
            <dd>{{locationLabel}}</dd>
          </div>
          <div class="metadata-item">
            <dt>Received</dt>
            <dd>
              @if (receivedValue) {
                {{receivedValue | smalltime}}
              } @else {
                Received time unavailable
              }
            </dd>
          </div>
          <div class="metadata-item">
            <dt>Duration</dt>
            <dd>
              @if (hasDuration) {
                {{response.duration | secs}}
              } @else {
                Duration unavailable
              }
            </dd>
          </div>
          <div class="metadata-item metadata-topic">
            <dt>Topic</dt>
            <dd>{{topicLabel}}</dd>
          </div>
          <div class="metadata-item metadata-contact">
            <dt>Contact</dt>
            <dd>
              @if (contactEmail || contactPhone) {
                <span class="contact-values">
                  @if (contactEmail) {
                    <a href="mailto:{{contactEmail}}">{{contactEmail}}</a>
                  }
                  @if (contactEmail && contactPhone) {
                    <span aria-hidden="true">, </span>
                  }
                  @if (contactPhone) {
                    <a href="tel:{{contactPhone}}">{{contactPhone}}</a>
                  }
                </span>
              } @else {
                No contact details
              }
            </dd>
          </div>
        </dl>

        <div class="editor-statuses" aria-label="Selected response status">
          <span class="editor-status" [class.status-published]="response.published"
            [class.status-draft]="!response.published">{{publishStatusLabel}}</span>
          <span class="editor-status" [class.status-transcript]="hasTranscript"
            [class.status-muted]="!hasTranscript">{{transcriptStatusLabel}}</span>
          <span class="editor-status" [class.status-audio]="audioStatus === 'available'"
            [class.status-muted]="audioStatus !== 'available'">{{audioStatusLabel}}</span>
        </div>
      </section>

      <section class="editor-section audio-section" aria-labelledby="audio-section-title">
        <div class="section-heading">
          <h3 id="audio-section-title">Audio</h3>
          <span class="section-context">{{audioStatusLabel}}</span>
        </div>
        <div id="player" role="group" aria-label="Audio player controls" aria-describedby="audio-status">
          @if (isPlaying()) {
            <button type="button" class="player-control" (click)="Pause()" title="Pause audio">
              <svg class="cms-icon" aria-hidden="true" focusable="false"><use href="/images/cms-actions.svg#pause"></use></svg>
              <span>Pause</span>
            </button>
          } @else {
            <button type="button" class="player-control" (click)="Play()" title="Play audio"
              [disabled]="!canPlayAudio" [attr.aria-busy]="audioLoading">
              <svg class="cms-icon" aria-hidden="true" focusable="false"><use href="/images/cms-actions.svg#play"></use></svg>
              <span>{{audioLoading ? 'Loading...' : 'Play'}}</span>
            </button>
          }
          <button type="button" class="player-control" (click)="Stop()" title="Stop audio" [disabled]="!canStopAudio">
            <svg class="cms-icon" aria-hidden="true" focusable="false"><use href="/images/cms-actions.svg#stop"></use></svg>
            <span>Stop</span>
          </button>
          <a class="player-control" href="/api/private/r/audio/{{response.id}}/m4a/{{response.topic}}-{{response.id}}-{{response.name}}.m4a"
            download="{{response.topic}}-{{response.id}}-{{response.name}}.m4a" target="_blank" rel="noopener" title="Download audio">
            <svg class="cms-icon" aria-hidden="true" focusable="false"><use href="/images/cms-actions.svg#download"></use></svg>
            <span>Download</span>
          </a>
          <div id="times">
            <span class="visually-hidden">Audio position:</span>
            <span class="time">{{lastPosition | secs}}</span>
            <span aria-hidden="true"> / </span>
            <span class="visually-hidden">Duration:</span>
            <span class="time">{{duration | secs}}</span>
          </div>
        </div>
        <div id="progressbar" role="progressbar" aria-label="Audio playback progress"
          aria-valuemin="0" aria-valuemax="100" [attr.aria-valuenow]="progressBarPercent()">
          <span id="progress" [style.width.%]="progressBarPercent()"></span>
        </div>
        <div id="audio-status" class="audio-status" aria-live="polite">
          @if (audioError) {
            <span class="audio-error" role="alert">{{audioError}}</span>
            <button type="button" class="retry-audio" (click)="retryAudio()" [disabled]="audioLoading">Retry audio</button>
          } @else if (audioStatusMessage) {
            <span [class.audio-busy]="audioLoading">{{audioStatusMessage}}</span>
          }
        </div>
      </section>

      <section class="editor-section transcript-section" aria-labelledby="transcript-section-title">
        <div class="section-heading">
          <h3 id="transcript-section-title">Transcript</h3>
          <span class="section-context">{{transcriptStatusLabel}}</span>
        </div>
        <div id="transcript">
          <label class="visually-hidden" for="transcript-draft">Transcript</label>
          <textarea id="transcript-draft" [ngModel]="transcriptDraft"
            (ngModelChange)="onTranscriptDraftChange($event)" [readOnly]="isSavingTranscript"
            class="longer_input" rows="6" placeholder="transcript of the audio"
            title="Correct the transcription if needed."></textarea>
          <div class="save-controls">
            <button type="button" (click)="updateTranscript()" class="save btn btn-primary"
              [disabled]="!response || !isTranscriptDirty || isSavingTranscript"
              [attr.aria-busy]="isSavingTranscript" aria-describedby="transcript-save-status">
              @if (isSavingTranscript) {
                Saving...
              } @else if (transcriptSaveMessage && !isTranscriptDirty) {
                Saved
              } @else {
                Save transcript
              }
            </button>
          </div>
          <div id="transcript-save-status" class="transcript-status" aria-live="polite">
            @if (transcriptSaveError) {
              <span class="save-status save-error" role="alert">{{transcriptSaveError}}</span>
            } @else if (isSavingTranscript) {
              <span class="save-status save-saving">Saving transcript...</span>
            } @else if (transcriptSaveMessage) {
              <span class="save-status save-success">{{transcriptSaveMessage}}</span>
            } @else if (isTranscriptDirty) {
              <span class="save-status save-dirty">Unsaved changes</span>
            }
          </div>
        </div>
      </section>

      <section class="editor-section review-section" aria-labelledby="review-section-title">
        <div class="section-heading">
          <h3 id="review-section-title">Review</h3>
          <span class="editor-status" [class.status-published]="response.published"
            [class.status-draft]="!response.published">{{publishStatusLabel}}</span>
        </div>
        <p class="review-copy">
          @if (response.published) {
            This response is currently published.
          } @else {
            This response is currently a draft.
          }
        </p>
        <button type="button" class="review-action btn btn-primary" (click)="requestPublishToggle()"
          [disabled]="publishUpdating" [attr.aria-busy]="publishUpdating" [title]="publishActionTitle">
          <svg class="cms-icon" aria-hidden="true" focusable="false"><use [attr.href]="response.published ? '/images/cms-actions.svg#unpublish' : '/images/cms-actions.svg#publish'"></use></svg>
          {{publishActionLabel}}
        </button>
        <div class="review-status" aria-live="polite">
          @if (publishMessage) {
            <span [class.review-error]="publishMessageIsError"
              [attr.role]="publishMessageIsError ? 'alert' : 'status'">{{publishMessage}}</span>
          }
        </div>
      </section>
    </div>
  </article>
}
